/**
 * Utilitário para envio de eventos GA4 via Google Tag Manager
 */

/**
 * Envia um evento para o dataLayer do GTM
 * @param {string} eventName - Nome do evento
 * @param {object} eventParams - Parâmetros adicionais do evento
 */
export const sendGA4Event = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined') {
    const dataLayer = window.dataLayer;
    const eventPayload = {
      event: eventName,
      ...eventParams
    };

    if (dataLayer) {
      dataLayer.push(eventPayload);
    }

    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'ga4_event', payload: eventPayload }, '*');
    }
    
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ga4_event', payload: eventPayload }));
    }
  }
};

/**
 * Eventos de Calendário
 */
export const trackCalendarOpen = (productType) => {
  sendGA4Event('calendar_open', {
    product_type: productType, // 'hotel' ou 'flight'
    event_category: 'search_interaction',
    event_label: 'calendar_opened'
  });
};

export const trackDateSelected = (productType, dateType, date) => {
  sendGA4Event('date_selected', {
    product_type: productType,
    date_type: dateType, // 'checkin', 'checkout', 'departure', 'return'
    selected_date: date,
    event_category: 'search_interaction',
    event_label: `${dateType}_selected`
  });
};

export const trackDateRangeComplete = (productType, startDate, endDate, nights) => {
  sendGA4Event('date_range_complete', {
    product_type: productType,
    start_date: startDate,
    end_date: endDate,
    nights: nights,
    event_category: 'search_interaction',
    event_label: 'dates_completed'
  });
};

/**
 * Eventos de Origem/Destino
 */
export const trackLocationFieldFocus = (productType, fieldType) => {
  sendGA4Event('location_field_focus', {
    product_type: productType,
    field_type: fieldType, // 'origin', 'destination'
    event_category: 'search_interaction',
    event_label: `${fieldType}_focused`
  });
};

export const trackLocationSearch = (productType, fieldType, searchTerm) => {
  sendGA4Event('location_search', {
    product_type: productType,
    field_type: fieldType,
    search_term: searchTerm,
    event_category: 'search_interaction',
    event_label: `${fieldType}_search`
  });
};

export const trackLocationSelected = (productType, fieldType, locationCode, locationName, locationType) => {
  sendGA4Event('location_selected', {
    product_type: productType,
    field_type: fieldType,
    location_code: locationCode,
    location_name: locationName,
    location_type: locationType, // 'city', 'airport'
    event_category: 'search_interaction',
    event_label: `${fieldType}_selected`
  });
};

export const trackLocationSwap = (productType) => {
  sendGA4Event('location_swap', {
    product_type: productType,
    event_category: 'search_interaction',
    event_label: 'origin_destination_swapped'
  });
};

/**
 * Eventos de Validação
 */
export const trackValidationError = (productType, errorType, errorMessage) => {
  sendGA4Event('validation_error', {
    product_type: productType,
    error_type: errorType, // 'same_origin_destination', 'missing_field', 'invalid_date'
    error_message: errorMessage,
    event_category: 'search_error',
    event_label: errorType
  });
};

/**
 * Eventos de Ocupantes/Passageiros
 */
export const trackOccupantsOpen = (productType) => {
  sendGA4Event('occupants_open', {
    product_type: productType,
    event_category: 'search_interaction',
    event_label: 'occupants_modal_opened'
  });
};

export const trackOccupantsChange = (productType, occupantType, quantity, action) => {
  sendGA4Event('occupants_change', {
    product_type: productType,
    occupant_type: occupantType, // 'adult', 'child', 'infant'
    quantity: quantity,
    action: action, // 'increase', 'decrease'
    event_category: 'search_interaction',
    event_label: `${occupantType}_${action}`
  });
};

export const trackOccupantsComplete = (productType, adults, children, infants, totalOccupants) => {
  sendGA4Event('occupants_complete', {
    product_type: productType,
    adults: adults,
    children: children || 0,
    infants: infants || 0,
    total_occupants: totalOccupants,
    event_category: 'search_interaction',
    event_label: 'occupants_confirmed'
  });
};

export const trackTooltipView = (productType, tooltipType) => {
  sendGA4Event('tooltip_view', {
    product_type: productType,
    tooltip_type: tooltipType, // 'age_rules_flight', 'age_rules_hotel'
    event_category: 'search_interaction',
    event_label: `${tooltipType}_viewed`
  });
};

/**
 * Eventos do Botão Buscar
 */
export const trackSearchButtonClick = (productType, searchParams) => {
  sendGA4Event('search_button_click', {
    product_type: productType,
    has_origin: !!searchParams.origin,
    has_destination: !!searchParams.destination,
    has_dates: !!searchParams.startDate && !!searchParams.endDate,
    has_occupants: !!searchParams.occupants,
    event_category: 'search_action',
    event_label: 'search_button_clicked'
  });
};

export const trackSearchSuccess = (productType, searchParams) => {
  const params = {
    product_type: productType,
    event_category: 'search_action',
    event_label: 'search_submitted_success'
  };

  if (productType === 'flight') {
    params.origin = searchParams.origin;
    params.destination = searchParams.destination;
    params.departure_date = searchParams.departureDate;
    params.return_date = searchParams.returnDate;
    params.class = searchParams.class;
    params.adults = searchParams.adults;
    params.children = searchParams.children || 0;
    params.infants = searchParams.infants || 0;
    params.trip_type = searchParams.tripType; // 'roundtrip', 'oneway'
  } else if (productType === 'hotel') {
    params.destination = searchParams.destination;
    params.checkin_date = searchParams.checkinDate;
    params.checkout_date = searchParams.checkoutDate;
    params.nights = searchParams.nights;
    params.rooms = searchParams.rooms || 1;
    params.adults = searchParams.adults;
    params.children = searchParams.children || 0;
  }

  sendGA4Event('search_success', params);
};

export const trackSearchError = (productType, errorType, errorMessage, searchParams) => {
  sendGA4Event('search_error', {
    product_type: productType,
    error_type: errorType,
    error_message: errorMessage,
    has_origin: !!searchParams.origin,
    has_destination: !!searchParams.destination,
    has_dates: !!searchParams.startDate && !!searchParams.endDate,
    has_occupants: !!searchParams.occupants,
    event_category: 'search_error',
    event_label: `search_failed_${errorType}`
  });
};

/**
 * Eventos de Sugestões
 */
export const trackSuggestionDisplay = (productType, fieldType, suggestionsCount, hasCategories) => {
  sendGA4Event('suggestions_display', {
    product_type: productType,
    field_type: fieldType,
    suggestions_count: suggestionsCount,
    has_categories: hasCategories,
    event_category: 'search_interaction',
    event_label: 'suggestions_displayed'
  });
};

export const trackSuggestionClick = (productType, fieldType, suggestionType, suggestionPosition) => {
  sendGA4Event('suggestion_click', {
    product_type: productType,
    field_type: fieldType,
    suggestion_type: suggestionType, // 'city', 'airport'
    suggestion_position: suggestionPosition,
    event_category: 'search_interaction',
    event_label: `${suggestionType}_suggestion_clicked`
  });
};

/**
 * Eventos de Tempo de Interação
 */
export const trackFieldTimeSpent = (productType, fieldType, timeInSeconds) => {
  sendGA4Event('field_time_spent', {
    product_type: productType,
    field_type: fieldType,
    time_seconds: timeInSeconds,
    event_category: 'search_interaction',
    event_label: `${fieldType}_time_spent`
  });
};

export const trackFormAbandon = (productType, lastInteractedField, completionPercentage) => {
  sendGA4Event('form_abandon', {
    product_type: productType,
    last_interacted_field: lastInteractedField,
    completion_percentage: completionPercentage,
    event_category: 'search_abandon',
    event_label: 'form_abandoned'
  });
};

export const trackHotelRedoSearch = () => {
  sendGA4Event('redo_search', {
    click: 'VERDADEIRO',
    event_category: 'hotel_journey',
    event_label: 'redo_search'
  });
};

export const trackHotelSearch = (params) => {
  sendGA4Event('hotel_search', {
    location: params.location,
    search_name: params.search_name || '',
    check_in: params.check_in,
    check_out: params.check_out,
    nights: params.nights,
    hosts: params.hosts,
    adults: params.adults,
    children: params.children,
    event_category: 'hotel_journey',
    event_label: 'hotel_search'
  });
};

export const trackHotelFilter = (params) => {
  sendGA4Event('filter', {
    points: params.points,
    search_name: params.search_name || '',
    stars: params.stars,
    refund: params.refund,
    meal: params.meal,
    event_category: 'hotel_journey',
    event_label: 'filter'
  });
};

export const trackHotelSelectHotel = (params) => {
  sendGA4Event('select_hotel', {
    location: params.location,
    check_in: params.check_in,
    check_out: params.check_out,
    hotel_name: params.hotel_name,
    hosts: params.hosts,
    nights: params.nights,
    adults: params.adults,
    children: params.children,
    stars: params.stars,
    event_category: 'hotel_journey',
    event_label: 'select_hotel'
  });
};

export const trackHotelSeeAvailability = (params) => {
  sendGA4Event('see_availability', {
    location: params.location,
    check_in: params.check_in,
    check_out: params.check_out,
    hotel_name: params.hotel_name,
    hosts: params.hosts,
    nights: params.nights,
    adults: params.adults,
    children: params.children,
    stars: params.stars,
    event_category: 'hotel_journey',
    event_label: 'see_availability'
  });
};

export const trackHotelReserveRoom = (params) => {
  sendGA4Event('reserve_room', {
    points: params.points,
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    location: params.location,
    check_in: params.check_in,
    check_out: params.check_out,
    meal: params.meal,
    hosts: params.hosts,
    refund: params.refund,
    event_category: 'hotel_journey',
    event_label: 'reserve_room'
  });
};

export const trackHotelSelectRoom = (params) => {
  sendGA4Event('select_room', {
    points: params.points,
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    location: params.location,
    check_in: params.check_in,
    check_out: params.check_out,
    meal: params.meal,
    hosts: params.hosts,
    refund: params.refund,
    event_category: 'hotel_journey',
    event_label: 'select_room'
  });
};

export const trackHotelGuests = (params) => {
  sendGA4Event('guests', {
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    points: params.points,
    total: params.total,
    check_in: params.check_in,
    check_out: params.check_out,
    nights: params.nights,
    stars: params.stars,
    hosts: params.hosts,
    event_category: 'hotel_journey',
    event_label: 'guests'
  });
};

export const trackHotelPayment = (params) => {
  sendGA4Event('payment', {
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    points: params.points,
    cash: params.cash,
    total_value: params.total_value,
    total_points: params.total_points,
    check_in: params.check_in,
    check_out: params.check_out,
    payment: params.payment,
    nights: params.nights,
    stars: params.stars,
    hosts: params.hosts,
    event_category: 'hotel_journey',
    event_label: 'payment'
  });
};

export const trackHotelBooking = (params) => {
  sendGA4Event('booking', {
    order_id: params.order_id,
    hotel_name: params.hotel_name,
    room_name: params.room_name,
    points: params.points,
    cash: params.cash,
    total_value: params.total_value,
    total_points: params.total_points,
    check_in: params.check_in,
    check_out: params.check_out,
    nights: params.nights,
    stars: params.stars,
    hosts: params.hosts,
    event_category: 'hotel_journey',
    event_label: 'booking'
  });
};

export const trackHotelCallback = (params) => {
  sendGA4Event('callback', {
    status: params.status,
    action: params.action,
    error: params.error || '',
    event_category: 'hotel_journey',
    event_label: `callback_${params.action}_${params.status}`
  });
};

/**
 * Eventos de Passagens Aéreas (Safra)
 */

export const trackFlightSearch = (params) => {
  sendGA4Event('flights_search', {
    departure: params.departure,
    arrival: params.arrival,
    depating: params.depating,
    returning: params.returning,
    passengers: params.passengers,
    class: params.class,
    type: params.type,
    adults: params.adults,
    children: params.children,
    baby: params.baby
  });
};

export const trackBannerImpression = (params) => {
  sendGA4Event('banner_impression', {
    index: params.index || '',
    custom_title: params.customTitle || '',
    content_type: params.contentType || '',
    Nome_Da_Tela: 'marketplace-home-viagens',
    data_inicio: params.dataInicio || '',
    data_termino: params.dataTermino || '',
    url_imagem: params.urlImage || '',
    funcionalidades: 'viagens',
    id_da_tag: 'safra.viagens.2',
    webview: 'safra-viagens',
    departure: params.departure || '',
    arrival: params.arrival || '',
    depating: params.depating || '',
    returning: params.returning || '',
    class: params.classType || '',
    type: params.type || '',
    adults: params.adults || '',
    children: params.children || '',
    baby: params.baby || '',
    location: params.location || '',
    check_in: params.check_in || '',
    check_out: params.check_out || '',
    nights: params.nights || '',
    url_link: ''
  })
}

export const trackBannerClick = (params) => {
  sendGA4Event('banner_click', {
    elemento: 'banner-carrossel',
    index: params.index || '',
    custom_title: params.customTitle || '',
    content_type: params.contentType || '',
    Nome_Da_Tela: 'marketplace-home-viagens',
    data_inicio: params.dataInicio || '',
    data_termino: params.dataTermino || '',
    url_imagem: params.urlImage || '',
    funcionalidades: 'viagens',
    id_da_tag: 'safra.viagens.2',
    webview: 'safra-viagens',
    departure: params.departure || '',
    arrival: params.arrival || '',
    depating: params.depating || '',
    returning: params.returning || '',
    class: params.class || '',
    type: params.type || '',
    adults: params.adults || '',
    children: params.children || '',
    baby: params.baby || '',
    location: params.location || '',
    search_name: params.search_name || '',
    check_in: params.check_in || '',
    check_out: params.check_out || '',
    nights: params.nights || '',
    hosts: params.hosts || '',
    url_link: ''
  })
}

export const trackFlightSelect = (params) => {
  sendGA4Event('select_flight', {
    departure: params.departure,
    arrival: params.arrival,
    depating: params.depating,
    returning: params.returning,
    passengers: params.passengers,
    class: params.class,
    type: params.type,
    adults: params.adults,
    children: params.children,
    baby: params.baby,
    points: params.points,
    event_category: 'flight_journey',
    event_label: 'select_flight'
  });
};

export const trackFligthRescue = (params) => {
  sendGA4Event('flights_rescue', {
    departure: params.departure,
    arrival: params.arrivel,
    depating: params.depating,
    returning: params.returning,
    passengers: params.passengers,
    class: params.class,
    type: params.type,
    adults: params.adults,
    children: params.children,
    baby: params.baby
  });
}

export const trackFlightGuests = (params) => {
  sendGA4Event('flights_guests', params);
}

export const trackFlightPayment = (params) => {
  sendGA4Event('payment', {
    departure: params.departure,
    arrival: params.arrival,
    depating: params.depating,
    returning: params.returning,
    passengers: params.passengers,
    class: params.class,
    type: params.type,
    adults: params.adults,
    children: params.children,
    baby: params.baby,
    payment: params.payment,
    total_cash: params.total_cash,
    cash: params.cash,
    total_points: params.total_points,
    points: params.points,
    event_category: 'flight_journey',
    event_label: 'payment'
  });
};

export const trackFlightConfirmation = (params) => {
  sendGA4Event('confirmation', {
    departure: params.departure,
    arrival: params.arrivel,
    depating: params.depating,
    returning: params.returning,
    passengers: params.passengers,
    class: params.class,
    type: params.type,
    adults: params.adults,
    children: params.children,
    baby: params.baby,
    order_id: params.order_id,
    total_cash: params.total_cash,
    cash: params.cash,
    total_points: params.total_points,
    points: params.points,
    event_category: 'flight_journey',
    event_label: 'confirmation'
  });
};
